import React from 'react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import BusinessIcon from '@mui/icons-material/Business'
import CalendarIcon from '@mui/icons-material/CalendarToday'
import PolicyIcon from '@mui/icons-material/Description'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import {
  Avatar,
  Box,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material'

import { AxleAccount, AxlePolicy } from 'common/interfaces'
import { deleteBtnStyle } from 'common/styles'

interface ConnectedAccountProps {
  account: AxleAccount | null
  policy: AxlePolicy | null
  restartConnection: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

const ConnectedAccount: React.FC<ConnectedAccountProps> = ({
  account,
  policy,
  restartConnection,
}) => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const getVehicles = (properties: any[]): any[] => {
    return properties.filter((property: any) => property.type === 'vehicle')
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: 3,
        p: 3,
        mx: 'auto',
        mt: 4,
        width: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'text.primary' }}>
          Connected Insurance Account
        </Typography>
        <Box
          sx={{
            backgroundColor: 'success.main',
            color: 'common.white',
            borderRadius: 12,
            px: 2,
            py: 0.5,
            fontSize: '0.875rem',
          }}
        >
          Connected
        </Box>
      </Box>

      {/* Account Information */}
      <Box sx={{ mb: 3 }}>
        {/* Policy Number */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            <PolicyIcon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Policy Number
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {policy?.policyNumber}
            </Typography>
          </Box>
        </Box>

        {/* Account Holder */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
            <AccountCircleIcon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Account Holder
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {account?.firstName} {account?.lastName}
            </Typography>
          </Box>
        </Box>

        {/* Carrier Information */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
            <BusinessIcon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Carrier Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {account?.carrier}
            </Typography>
          </Box>
        </Box>

        {/* Coverage Period */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
            <CalendarIcon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Coverage Period
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {policy?.effectiveDate ? formatDate(policy?.effectiveDate) : 'None'} -{' '}
              {policy?.expirationDate ? formatDate(policy?.expirationDate) : 'None'}
            </Typography>
          </Box>
        </Box>

        {/* Vehicles Covered */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            Vehicles Covered
          </Typography>
          <List>
            {policy?.properties &&
              getVehicles(policy?.properties).map((vehicle, index) => (
                <ListItem key={index}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <DirectionsCarIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      (vehicle.data.year as string) +
                      ' ' +
                      (vehicle.data.make as string) +
                      ' ' +
                      (vehicle.data.model as string)
                    }
                  />
                </ListItem>
              ))}
          </List>
        </Box>
      </Box>

      {/* Footer Button */}
      <Button variant="outlined" fullWidth sx={deleteBtnStyle} onClick={restartConnection}>
        Restart Connection
      </Button>
    </Box>
  )
}

export default ConnectedAccount
